import { onScreenAll } from './visible';

export type AgentBridge = {
  currentPath(): string | null;
  pageState(): Record<string, unknown> | null;
  /** 조회 전 표 형태의 필수 체크목록을 모두 켠다. 저장·삭제 상태에는 손대지 않는다. */
  setCheckedRows?(stateKey: string, checkKey?: string, checked?: boolean): Promise<boolean>;
  /** 한 행만 체크한다. 갚을 미수 한 건을 고르는 자리 — 위젯 클릭이 안 먹는다. */
  setRowCheck?(stateKey: string, index: number, checkKey?: string, checked?: boolean): Promise<boolean>;
  /** 체크칸 하나를 켜고 끈다. 위젯이 미루는 사이 클릭이 삼켜지는 자리를 위한 통로. */
  setCheck?(name: string, checked?: boolean): Promise<boolean>;
  /** 읽기 전용 상세 조회를 위해 목록의 한 행에 포커스를 준다. 저장 상태는 바꾸지 않는다. */
  focusRow?(stateKey: string, match: Record<string, string>): Promise<boolean>;
  /**
   * 우측 상단 전역 사업장 선택기의 선택지. '전체' 같은 조회용 와일드카드는 빼고
   * 실제 사업장만 준다 — 순회하면 같은 행을 두 번 더하게 된다.
   */
  businessSites?(): { code: string; name: string; current: boolean }[];
  /**
   * 지금 로그인 문맥의 사업장 코드. 선택지에 없는 값(전사 `ZZ` 등)일 수 있어
   * `businessSites()` 의 `current` 만으로는 알 수 없다. 순회 뒤 되돌릴 곳이다.
   */
  currentBusinessSite?(): string | null;
  /** 전역 사업장 문맥만 바꾼다. 저장 중인 화면이면 앱 쪽에서 false로 거부한다. */
  switchBusinessSite?(code: string): Promise<boolean>;
  /** 열어 둔 탭들. 구버전 앱에는 없을 수 있다. */
  openTabs?(): { path: string; name: string }[];
  /** 앱이 서버를 기다리는 중인가. 구버전 앱에는 없을 수 있다. */
  busy?(): boolean;
  /**
   * 그 화면까지 펼쳐야 할 상위 메뉴 id 들. 위에서 아래 순서다.
   * 메뉴에 없으면 `null`. 구버전 앱에는 없을 수 있다.
   */
  menuChain?(routePath: string): string[] | null;
  /**
   * 사람이 이번 대화에 올린 파일을 **지금 화면의 문서**에 붙인다.
   *
   * **문서 번호도 파일도 앱이 안다.** 처음에는 계획이 `appnum` 을 내게 했는데, 그 값은
   * 화면 상태에서 만들어지는 것이라(`JA001-${perid}`, `TB_E611_PLC_${actcd}`) 모델이
   * 알 길이 없다. 그래서 프롬프트가 「모르면 이 갈래를 고르지 마라」라고 적었고,
   * 결과적으로 **아무도 고르지 않는 갈래**가 됐다. 화면이 정하게 바꾼다.
   *
   * 앱은 이미 두 번 부르는 길을 갖고 있다(`tempUpload` 다음 `attachment`).
   *
   * 붙인 수를 돌려준다. 못 붙이면 이유를 `notices` 에 담는다 — 던지지 않는다.
   */
  attach?(): Promise<{ attached: number; notices: string[] }>;
  /**
   * 서버에 맡긴 오래 걸리는 일의 대기열(`waitQueueStore`). 읽기만 한다 — 결과(`response`)와
   * 완료 뒤 할 일(`callback`)은 싣지 않는다. 구버전 앱에는 없을 수 있다.
   */
  waitQueue?(): { key: string; title: string; startedAt: number; done: boolean }[];
};

/** 앱이 createStore 에서 걸어 둔 읽기 전용 파사드. */
export function bridge(): AgentBridge {
  const value = (window as any).__elmanAgent;
  if (!value) throw new Error('__elmanAgent 가 없다. 앱이 로드되지 않았거나 구버전이다');
  return value as AgentBridge;
}

/**
 * 화면의 상세(초안) 상태.
 *
 * 상세를 담는 이름이 화면마다 다르다 — 휴가·출장은 `data`, 견적서는 `content`,
 * 민원접수는 검색 팝업이 고른 값을 `focusedDetailResister` 에 넣는다. 한 화면이 둘을
 * 같이 갖기도 한다: 민원접수는 접수일자를 `data` 에, 현장 코드를 `focusedDetailResister`
 * 에 둔다. 그래서 하나를 고르지 않고 겹쳐 본다 — 같은 이름이면 **값이 있는 쪽**이 이긴다.
 * 빈 문자열이 이기면 팝업으로 제대로 고른 현장을 "못 골랐다" 로 뒤집게 된다.
 */
/** 화면에 그려진 칸 → 보이는 값. 앵커가 입력칸 자체이기도 하고 감싼 것이기도 하다. */
function shownValues(): Map<string, string> {
  const shown = new Map<string, string>();
  // 보이는 것만 — 탭으로 살아 있는 앞 화면의 칸이 섞이면 엉뚱한 객체가 이긴다.
  for (const element of onScreenAll('[data-el-field]')) {
    const name = (element.getAttribute('data-el-field') ?? '').split('__')[0];
    if (!name || shown.has(name)) continue;
    const input = element.matches('input, textarea, select')
      ? element
      : element.querySelector('input, textarea, select');
    if (input) shown.set(name, (input as HTMLInputElement).value ?? '');
  }
  return shown;
}

const bare = (value: unknown): string => String(value ?? '').replace(/[^0-9A-Za-z가-힣]/g, '');

/** 보이는(빈 것이 아닌) 칸 가운데 그 객체가 같은 값을 든 수. */
function agreement(record: Record<string, unknown>, shown: Map<string, string>): number {
  let count = 0;
  // 앱은 downlevelIteration 없이 컴파일한다 — Map 을 바로 돌리면 tsc 가 막는다.
  for (const [name, value] of Array.from(shown)) {
    if (!bare(value)) continue;
    if (Object.prototype.hasOwnProperty.call(record, name) && bare(record[name]) === bare(value)) count += 1;
  }
  return count;
}

/**
 * 이름으로 찾은 상세보다 **보이는 칸과 더 많이 맞는 객체**.
 *
 * 보수현장등록은 폼을 `actInfo` 에 묶고, 상태에는 빈 `data` 도 있다. 이름으로 `data` 를
 * 집으면 넣은 팩스번호를 빈 값으로 읽어 실패로 뒤집는다(2026-09-25). 칸 **하나**가 아니라
 * 보이는 칸 전체로 견주므로, 방금 넣은 값 하나 때문에 엉뚱한 객체로 옮겨 가지는 않는다.
 */
function betterRecord(
  state: Record<string, unknown> | null | undefined,
  named: Record<string, unknown>,
): Record<string, unknown> | null {
  const shown = shownValues();
  const base = agreement(named, shown);
  let best: Record<string, unknown> | null = null;
  let bestScore = base;
  for (const detail of Object.values(state ?? {})) {
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) continue;
    const score = agreement(detail as Record<string, unknown>, shown);
    if (score > bestScore) { best = detail as Record<string, unknown>; bestScore = score; }
  }
  return best;
}

export function detailState(): Record<string, unknown> | null {
  const state = bridge().pageState();
  const merged: Record<string, unknown> = {};
  let found = false;
  // 상세를 부르는 이름은 화면마다 다르다. `focusedDetail…` 은 화면 이름이 뒤에 붙는
  // 한 벌이라(`focusedDetailResister`·`focusedDetailBreakComplete`) 이름을 하나씩
  // 적어 두면 새 화면마다 빠진다 — 고장처리결과에서 값이 제대로 들어갔는데도
  // `고르지 못했습니다` 로 뒤집혔다.
  const detailKeys = [
    'data',
    'content',
    ...Object.keys(state ?? {}).filter((key) => key.startsWith('focusedDetail')),
  ];
  for (const key of detailKeys) {
    const detail = state?.[key];
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) continue;
    found = true;
    for (const [name, value] of Object.entries(detail as Record<string, unknown>)) {
      const kept = merged[name];
      const empty = kept === undefined || kept === null || String(kept).trim() === '';
      if (empty) merged[name] = value;
    }
  }
  if (found) {
    const better = betterRecord(state, merged);
    // 더 맞는 객체가 먼저고, 거기 없는 칸만 이름으로 찾은 것에서 채운다.
    return better ? { ...merged, ...better } : merged;
  }
  // **이름을 하나씩 적는 것으로는 끝이 없다.** 인사등록의 상세는 `basic` 이라 여기까지
  // 와서 `null` 이 됐고, 그러면 팝업으로 제대로 고른 값도 `고르지 못했습니다` 로
  // 뒤집히며 초안 검증은 조용히 통과한다 — 둘 다 실측했다.
  //
  // 그래서 이름이 아니라 **화면이 그리고 있는 칸 이름**으로 찾는다. 그 칸을 가진
  // 객체가 이 화면의 상세다.
  const anchored = new Set(Array.from(document.querySelectorAll('[data-el-field]'))
    .map((element) => (element.getAttribute('data-el-field') ?? '').split('__')[0])
    .filter(Boolean));
  if (!anchored.size) return null;
  for (const [key, detail] of Object.entries(state ?? {})) {
    if (detailKeys.includes(key)) continue;
    if (!detail || typeof detail !== 'object' || Array.isArray(detail)) continue;
    const names = Object.keys(detail as Record<string, unknown>);
    if (!names.some((one) => anchored.has(one))) continue;
    found = true;
    for (const [name, value] of Object.entries(detail as Record<string, unknown>)) {
      const kept = merged[name];
      const empty = kept === undefined || kept === null || String(kept).trim() === '';
      if (empty) merged[name] = value;
    }
  }
  return found ? merged : null;
}
